import {
  EnumEventType,
  EnumOrganizationMetadataDisabledApps,
  EventWithOccurrences,
  OrganizationsByIdsQuery,
  useOrganizationsByIdsLazyQuery,
} from "gql/generated";
import { useMemo, useEffect, useState } from "react";
import { Event } from "gql/generated";
import { getRecurrencePatternDates } from "shared/utils";
import { ModalMode } from "apps/events/types/ModalMode";

type EventPreviewProps = {
  defaultOrganizationId?: string;
  event: Event;
  mode: ModalMode;
  /**
   * Org IDs from the *chosen host operator's* active enrollments. On submit the
   * backend expands access.organizations to include these, so the Submit panel
   * must show them too. Scoped to the chosen host only — never the union of
   * every operator the current user can access.
   */
  hostActiveOrganizationIds?: string[];
};

export const useEventPreview = ({
  defaultOrganizationId,
  event,
  mode,
  hostActiveOrganizationIds,
}: EventPreviewProps) => {
  const [getOrganizations, { loading: organizationsLoading }] =
    useOrganizationsByIdsLazyQuery();

  /**
   * When the event changes, we need to fetch the organizations for the event.
   * A draft only carries its single creating org until it's submitted, so this
   * mirrors the backend's submit-time expansion (eventUpdate step 3) to preview
   * the full set of orgs the event will actually reach.
   */
  const [organizations, setOrganizations] = useState<
    OrganizationsByIdsQuery["organizationsByIds"]
  >([]);
  useEffect(() => {
    if (!event || !event._id) return;

    const eventOrgIds = (event?.access?.organizations
      .map(organization => organization?._entityId)
      .filter(Boolean) ?? []) as string[];

    const disconnectedOrgIds = new Set(
      (event?.access?.disconnectedOrganizations ?? []).filter(
        Boolean,
      ) as string[],
    );
    const hostOnlyOrgIds = new Set(
      (hostActiveOrganizationIds ?? []).filter(
        id => !eventOrgIds.includes(id) && !disconnectedOrgIds.has(id),
      ),
    );
    const orgIds = Array.from(new Set([...eventOrgIds, ...hostOnlyOrgIds]));

    const idsToFetch =
      orgIds.length > 0 ? orgIds : [defaultOrganizationId].filter(Boolean);

    if (idsToFetch.length > 0) {
      getOrganizations({
        variables: {
          ids: idsToFetch as string[],
        },
        onCompleted: data => {
          setOrganizations(
            data.organizationsByIds.filter(
              org =>
                !hostOnlyOrgIds.has(org._id) ||
                !org.metadata?.disabledApps?.includes(
                  EnumOrganizationMetadataDisabledApps.Events,
                ),
            ),
          );
        },
      });
    } else {
      setOrganizations([]);
    }
  }, [
    defaultOrganizationId,
    event,
    getOrganizations,
    hostActiveOrganizationIds,
  ]);

  /**
   * When the event changes, we need to get the first 5 occurrences of the event
   */
  const eventWithOccurrences = useMemo(() => {
    if (!event || !event._id) return;

    const obj = { ...event } as EventWithOccurrences;
    const eventType = obj.type;
    switch (eventType) {
      case EnumEventType.Single:
        obj.occurrences = [obj.startDate];
        break;
      case EnumEventType.Multi:
        obj.occurrences = obj.recurrencePattern?.multiDates ?? [];
        break;
      case EnumEventType.Recurring: {
        const skipDates = obj.recurrencePattern?.skipDates ?? [];
        obj.occurrences = getRecurrencePatternDates(
          obj.recurrencePattern,
          obj.startDate,
        )
          .filter(date => !skipDates.includes(date))
          .slice(0, 5);
        break;
      }
    }
    return obj;
  }, [event]);

  /**
   * When the mode changes, we need to update the content box and carousel box props
   */
  const { contentBoxProps, carouselBoxProps } = useMemo(() => {
    if (mode !== "approve" && mode !== "view") {
      return {
        contentBoxProps: {
          border: "1px solid",
          borderColor: "border.light",
          borderRadius: "200",
          overflow: "hidden",
        },
        carouselBoxProps: {
          position: "static",
        },
      };
    }
    return {
      contentBoxProps: {},
      carouselBoxProps: {},
    };
  }, [mode]);

  return {
    eventWithOccurrences,
    organizations,
    organizationsLoading,
    contentBoxProps,
    carouselBoxProps,
  };
};
